// src/pages/GatePasses/parts/GatePassFormModal.tsx
//
// Create form for a gate pass.
//   POST /gate-passes   (JSON)
//
// Early departure: created "pending", then approved by the school or by the
// parent via OTP in ViewGatePassModal (opened right after creation).
// Short leave: the same POST also records the exit — the student is "out"
// as soon as it's saved.
//
// `prefillStudent` comes from POST /gate-passes/scan, so a scanned student
// is shown straight away without another search call.
//
// The student is picked with GET /students/search (min 2 characters). The
// box also takes an ID-card scan: the scanner types the SP ID and presses
// Enter, which picks the exact match. Picking a student offers the
// father/mother on record as one-tap pickup options.

import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import apiClient from '../../../stateManagers/services/axiosInterceptor';
import { useSchoolDetailsStore } from '../../../stateManagers/hooks/useSchoolDetailsStore';
import { GlobalInputField } from '../../../components/GlobalComponents/GlobalInputField';
import { GlobalTextArea } from '../../../components/GlobalComponents/GlobalTextArea';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';
import {
  PASS_TYPE_META,
  PassType,
  REASON_OPTIONS,
  RELATION_ALONE,
  RELATION_OPTIONS,
  RETURN_QUICK_PICKS,
  StudentSearchResult,
  getInitials,
  mapSearchStudent,
} from './types';

// "18:24" → "6:24 pm" so the hint matches the time picker.
const clock12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return hhmm;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'am' : 'pm'}`;
};

interface GatePassFormModalProps {
  open: boolean;
  /** Preselected from the dashboard tab the user is on; can still be changed. */
  initialPassType?: PassType;
  /** Student returned by POST /gate-passes/scan — picked as soon as the form opens. */
  prefillStudent?: StudentSearchResult | null;
  onClose: () => void;
  /** Receives the new pass so the dashboard can switch to its tab and open it. */
  onSuccess: (created: { id: string; studentName: string; spStudentId: string; passType: PassType }) => void;
}

const emptyForm = {
  passType: '' as PassType | '',
  reason: '',
  reasonDetails: '',
  pickedUpBy: '',
  relation: '',
  contactNo: '',
  returnTime: '', // "HH:MM", short leave only, optional
};

// ── Small presentational pieces ─────────────────────────────────────────────

const PillGroup: React.FC<{
  options: { value: string; label: string; icon?: string; hint?: string }[];
  value: string;
  invalid: boolean;
  disabled: boolean;
  onChange: (v: string) => void;
}> = ({ options, value, invalid, disabled, onChange }) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      padding: invalid ? 8 : 0,
      border: invalid ? '1px solid #fca5a5' : 'none',
      borderRadius: 12,
      background: invalid ? 'rgba(239,68,68,.04)' : 'transparent',
    }}
  >
    {options.map((opt) => {
      const active = value === opt.value;
      return (
        <button
          key={opt.value}
          type="button"
          onClick={() => !disabled && onChange(opt.value)}
          className="gatepass-pill"
          disabled={disabled}
          style={{
            padding: opt.hint ? '10px 16px' : '8px 14px',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: `1.5px solid ${active ? '#3b82f6' : '#e5e7eb'}`,
            background: active ? 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)' : 'white',
            color: active ? 'white' : '#4b5563',
            boxShadow: active ? '0 4px 12px rgba(59, 130, 246, 0.25)' : 'none',
            transition: 'all .15s',
            opacity: disabled ? 0.7 : 1,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textAlign: 'left',
          }}
        >
          {opt.icon && <i className={opt.icon} style={{ fontSize: 13 }} />}
          <span>
            {opt.label}
            {opt.hint && (
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 500,
                  opacity: active ? 0.85 : 0.7,
                  marginTop: 2,
                }}
              >
                {opt.hint}
              </span>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

const SectionHeader: React.FC<{ icon: string; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 16,
      paddingBottom: 12,
      borderBottom: '1px solid rgba(229, 231, 235, 0.6)',
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 32,
        height: 32,
        borderRadius: 8,
        background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        color: 'white',
        flexShrink: 0,
      }}
    >
      <i className={icon} style={{ fontSize: 15 }} />
    </div>
    <span
      style={{
        color: '#1e3a8a',
        fontWeight: 700,
        fontSize: 14,
        letterSpacing: '0.02em',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

const FieldLabel: React.FC<{ text: string; required?: boolean }> = ({ text, required }) => (
  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a8a', marginBottom: 8 }}>
    {text}
    {required && <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>}
  </div>
);

const sectionCardStyle: React.CSSProperties = {
  background: 'rgba(255, 255, 255, 0.8)',
  borderRadius: 16,
  padding: 20,
  border: '1px solid rgba(59, 130, 246, 0.1)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
};

const studentAvatarStyle: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 10,
  objectFit: 'cover',
  flexShrink: 0,
  background: 'linear-gradient(135deg,#4169E1,#5B7CE6)',
  color: 'white',
  fontSize: 13,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const classLine = (s: StudentSearchResult): string =>
  [
    s.className && `Class ${s.className}`,
    s.section && `Sec ${s.section}`,
    s.rollNo !== null && `Roll ${s.rollNo}`,
  ]
    .filter(Boolean)
    .join(' · ');

// ── Component ───────────────────────────────────────────────────────────────

const GatePassFormModal: React.FC<GatePassFormModalProps> = ({
  open,
  initialPassType,
  prefillStudent = null,
  onClose,
  onSuccess,
}) => {
  const toast = useRef<Toast>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);

  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [form, setForm] = useState(emptyForm);
  const [student, setStudent] = useState<StudentSearchResult | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StudentSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [quickPick, setQuickPick] = useState<number | null>(null); // highlights the chip used
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...emptyForm, passType: initialPassType ?? '' });
    setStudent(prefillStudent);
    setSearchQuery('');
    setSearchResults([]);
    setSearching(false);
    setSaving(false);
    setTouched(false);
    setQuickPick(null);
    setIsClosing(false);
  }, [open, initialPassType, prefillStudent]);

  useEffect(
    () => () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isClosing) {
      const dialog = document.querySelector('.p-dialog');
      const mask = document.querySelector('.p-dialog-mask');
      if (dialog) dialog.classList.add('dialog-closing');
      if (mask) mask.classList.add('mask-closing');
    }
  }, [isClosing]);

  const setField = (key: keyof typeof emptyForm, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // ── Student search ─────────────────────────────────────────────────────────

  // Returns the rows it found ([] if a newer search replaced it).
  const runSearch = async (q: string): Promise<StudentSearchResult[]> => {
    const seq = ++searchSeqRef.current;
    setSearching(true);
    try {
      if (!currentSessionId) {
        setSearchResults([]);
        return [];
      }
      const res = await apiClient.get('/students/search', {
        params: { q, session_id: currentSessionId, page: 1, limit: 8 },
      });
      if (seq !== searchSeqRef.current) return []; // a newer search is in flight
      const rows: StudentSearchResult[] =
        res.data.status === 'success' ? (res.data.data || []).map(mapSearchStudent) : [];
      setSearchResults(rows);
      return rows;
    } catch {
      if (seq === searchSeqRef.current) setSearchResults([]);
      return [];
    } finally {
      if (seq === searchSeqRef.current) setSearching(false);
    }
  };

  // Enter (or a scanner's Enter): search right away, then pick the exact
  // SP ID match — or the only result.
  const pickFromScan = async (raw: string = searchQuery) => {
    const q = raw.trim();
    if (q.length < 2) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const rows = await runSearch(q);
    const exact = rows.find((s) => s.spStudentId.toLowerCase() === q.toLowerCase());
    const match = exact ?? (rows.length === 1 ? rows[0] : null);
    if (match) pickStudent(match);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (value.trim().length < 2) {
      searchSeqRef.current++;
      setSearchResults([]);
      setSearching(false);
      return;
    }
    searchDebounceRef.current = setTimeout(() => runSearch(value.trim()), 350);
  };

  const pickStudent = (s: StudentSearchResult) => {
    setStudent(s);
    setSearchQuery('');
    setSearchResults([]);
    // Pickup details belong to the previous student — start them over.
    setForm((prev) => ({
      ...prev,
      pickedUpBy: '',
      contactNo: '',
      relation: prev.relation === RELATION_ALONE ? prev.relation : '',
    }));
  };

  const fillPickup = (name: string, relation: string, contactNo: string) =>
    setForm((prev) => ({ ...prev, pickedUpBy: name, relation, contactNo }));

  // ── Validation ─────────────────────────────────────────────────────────────

  // Short leave only: going out alone needs no name / contact.
  const isAlone = form.passType === 'short_leave' && form.relation === RELATION_ALONE;
  const relationOptions =
    form.passType === 'short_leave' ? [RELATION_ALONE, ...RELATION_OPTIONS] : RELATION_OPTIONS;

  const pickReturnIn = (minutes: number) => {
    const d = new Date(Date.now() + minutes * 60_000);
    const pad = (n: number) => String(n).padStart(2, '0');
    setForm((prev) => ({ ...prev, returnTime: `${pad(d.getHours())}:${pad(d.getMinutes())}` }));
    setQuickPick(minutes);
  };

  const returnAt = (() => {
    if (form.passType !== 'short_leave' || !form.returnTime) return null;
    const [h, m] = form.returnTime.split(':').map(Number);
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return d;
  })();
  const returnInPast = !!returnAt && returnAt.getTime() <= Date.now();

  const missing = {
    student: !student,
    passType: !form.passType,
    reason: !form.reason,
    pickedUpBy: !isAlone && !form.pickedUpBy.trim(),
    relation: !form.relation,
    contactNo: !isAlone && !form.contactNo.trim(),
    // Optional — only wrong if it's set and already past.
    returnTime: form.passType === 'short_leave' && !!form.returnTime && returnInPast,
  };
  const canSave = !Object.values(missing).some(Boolean);
  const typeMeta = form.passType ? PASS_TYPE_META[form.passType] : null;
  const showError = (key: keyof typeof missing) => touched && missing[key];

  // ── Actions ────────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (saving) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  const handleSave = async () => {
    setTouched(true);
    if (!canSave || saving || !student) return;

    const details = form.reasonDetails.trim();
    const payload: Record<string, string> = {
      student_id: student.studentId,
      pass_type: form.passType,
      reason: details ? `${form.reason} — ${details}` : form.reason,
      picked_up_by: isAlone ? '' : form.pickedUpBy.trim(),
      relation: form.relation,
      contact_no: isAlone ? '' : form.contactNo.trim(),
    };
    if (returnAt) payload.expected_return_at = returnAt.toISOString();

    setSaving(true);
    try {
      const res = await apiClient.post('api/gate-passes', payload);
      if (res.data.status === 'success') {
        toast.current?.show({
          severity: 'success',
          summary: `${PASS_TYPE_META[form.passType as PassType].label} created`,
          detail:
            form.passType === 'short_leave'
              ? res.data.message || `${student.name} went out.`
              : `Pass for ${student.name} is waiting for approval.`,
          life: 4000,
        });
        const created = {
          id: String(res.data.gate_pass_id),
          studentName: student.name,
          spStudentId: student.spStudentId,
          passType: form.passType as PassType,
        };
        setTimeout(() => {
          onSuccess(created);
          handleClose();
        }, 300);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Failed',
          detail: res.data.message || 'The gate pass could not be created.',
          life: 5000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.message || 'Something went wrong while saving.',
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
      <div
        style={{
          width: 54,
          height: 54,
          borderRadius: 14,
          background: typeMeta ? typeMeta.gradient : 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 24,
          boxShadow: `0 8px 24px ${typeMeta ? typeMeta.border : 'rgba(59, 130, 246, 0.3)'}`,
          flexShrink: 0,
          border: '3px solid white',
          transition: 'background .2s',
        }}
      >
        <i className={typeMeta ? typeMeta.icon : 'pi pi-ticket'} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 20,
            fontWeight: 700,
            color: typeMeta ? typeMeta.color : '#1e40af',
            lineHeight: 1.2,
            marginBottom: 4,
          }}
        >
          {typeMeta ? `New ${typeMeta.label.toLowerCase()}` : 'New gate pass'}
        </div>
        <div style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
          {typeMeta ? typeMeta.hint : 'Choose whether the student is going home or coming back'}
        </div>
      </div>
    </div>
  );

  const pickupOptions = student
    ? [
        student.fatherName && {
          name: student.fatherName,
          relation: 'Father',
          contactNo: student.fatherNo,
        },
        student.motherName && {
          name: student.motherName,
          relation: 'Mother',
          contactNo: student.motherNo,
        },
      ].filter(Boolean) as { name: string; relation: string; contactNo: string }[]
    : [];

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <Dialog
        visible={open && !isClosing}
        onHide={handleClose}
        header={headerTemplate}
        style={{ width: '90vw', maxWidth: 920, borderRadius: 24, overflow: 'hidden' }}
        contentStyle={{
          padding: 18,
          background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
          maxHeight: 'calc(90vh - 120px)',
          overflowY: 'auto',
          border: '12px solid rgba(255, 255, 255, 0.8)',
          borderRadius: '0 0 24px 24px',
          borderTop: 'none',
        }}
        headerStyle={{
          background: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '20px 24px',
          borderRadius: '24px 24px 0 0',
          borderLeft: '12px solid rgba(255, 255, 255, 0.8)',
          borderRight: '12px solid rgba(255, 255, 255, 0.8)',
          borderTop: '12px solid rgba(255, 255, 255, 0.8)',
          margin: '-12px -12px 0 -12px',
        }}
        modal
        dismissableMask={!saving}
        draggable={false}
        resizable={false}
        blockScroll
      >
        <style>{`
          .p-dialog-mask {
            backdrop-filter: blur(4px);
            background-color: rgba(0, 0, 0, 0.25) !important;
            animation: maskFadeIn 0.35s ease;
          }
          .p-dialog-mask.mask-closing { animation: maskFadeOut 0.4s ease !important; }
          @keyframes maskFadeIn {
            from { opacity: 0; background-color: rgba(0, 0, 0, 0); }
            to { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
          }
          @keyframes maskFadeOut {
            from { opacity: 1; background-color: rgba(0, 0, 0, 0.25); }
            to { opacity: 0; background-color: rgba(0, 0, 0, 0); }
          }
          .p-dialog {
            animation: dialogOpen 0.35s cubic-bezier(0.4, 0, 0.2, 1);
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          }
          .p-dialog.dialog-closing { animation: dialogClose 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important; }
          @keyframes dialogOpen {
            from { opacity: 0; transform: scale(0.92) translateY(-20px); }
            to { opacity: 1; transform: scale(1) translateY(0); }
          }
          @keyframes dialogClose {
            from { opacity: 1; transform: scale(1) translateY(0); }
            to { opacity: 0; transform: scale(0.88) translateY(25px); }
          }
          .p-dialog .p-dialog-content::-webkit-scrollbar { width: 8px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-track { background: rgba(241, 245, 249, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb { background: rgba(148, 163, 184, 0.5); border-radius: 10px; }
          .p-dialog .p-dialog-content::-webkit-scrollbar-thumb:hover { background: rgba(100, 116, 139, 0.7); }
          .gatepass-pill:hover:not(:disabled) { border-color: #93c5fd !important; }
          .gatepass-search { display:flex; align-items:center; gap:10px; background:white; border:1.5px solid #e5e7eb; border-radius:12px; padding:10px 14px; transition:border-color .2s; }
          .gatepass-search:focus-within { border-color: rgba(59,130,246,.55); }
          .gatepass-search.invalid { border-color: #fca5a5; background: rgba(239,68,68,.03); }
          .gatepass-search input { flex:1; border:none; outline:none; background:transparent; font-size:14px; font-weight:500; color:#1f2937; }
          .gatepass-search input::placeholder { color:#9ca3af; font-weight:400; }
          .gatepass-result { display:flex; align-items:center; gap:12px; width:100%; padding:10px 12px; border:none; background:white; border-radius:10px; cursor:pointer; text-align:left; transition:background .12s; }
          .gatepass-result:hover { background: rgba(59,130,246,.06); }
          .gatepass-quickfill { display:inline-flex; align-items:center; gap:8px; padding:8px 12px; border-radius:10px; border:1.5px dashed #93c5fd; background:rgba(239,246,255,.7); color:#1e40af; font-size:12.5px; font-weight:600; cursor:pointer; }
          .gatepass-quickfill:hover:not(:disabled) { background: rgba(219,234,254,.9); }
          .gatepass-time { padding:10px 12px; border-radius:10px; border:1.5px solid #e5e7eb; font-size:14px; font-weight:600; color:#1f2937; background:white; outline:none; }
          .gatepass-time:focus { border-color: rgba(59,130,246,.55); }
          .gatepass-time.invalid { border-color: #fca5a5; }
          .gatepass-quick { padding:8px 14px; border-radius:20px; border:1.5px solid rgba(15,118,110,.3); background:white; color:#0f766e; font-size:12.5px; font-weight:700; cursor:pointer; transition:all .12s; }
          .gatepass-quick:hover:not(:disabled) { background:rgba(20,184,166,.08); }
          .gatepass-quick.active { background:#0f766e; border-color:#0f766e; color:white; }
          .gatepass-type-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
          @media (max-width: 640px) { .gatepass-type-grid { grid-template-columns:1fr; } }
          .gatepass-type-card { display:flex; align-items:center; gap:14px; padding:16px 18px; border-radius:16px; border:2px solid #e5e7eb; background:white; cursor:pointer; text-align:left; transition:all .18s; }
          .gatepass-type-card:hover:not(:disabled) { transform: translateY(-1px); }
          .gatepass-type-card:disabled { cursor:not-allowed; opacity:.7; }
        `}</style>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* ── Pass type — the first decision, shown as two big cards ── */}
          <div
            className="gatepass-type-grid"
            style={{
              padding: showError('passType') ? 8 : 0,
              border: showError('passType') ? '1px solid #fca5a5' : 'none',
              borderRadius: 18,
            }}
          >
            {(Object.keys(PASS_TYPE_META) as PassType[]).map((t) => {
              const meta = PASS_TYPE_META[t];
              const active = form.passType === t;
              return (
                <button
                  key={t}
                  type="button"
                  className="gatepass-type-card"
                  disabled={saving}
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      passType: t,
                      // "Alone" only exists for short leave.
                      relation: t === 'early_departure' && prev.relation === RELATION_ALONE ? '' : prev.relation,
                    }))
                  }
                  style={{
                    borderColor: active ? meta.color : '#e5e7eb',
                    background: active ? meta.bg : 'white',
                    boxShadow: active ? `0 8px 24px ${meta.border}` : '0 2px 8px rgba(15,23,42,.04)',
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      flexShrink: 0,
                      background: active ? meta.gradient : meta.bg,
                      color: active ? 'white' : meta.color,
                    }}
                  >
                    <i className={meta.icon} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: active ? meta.color : '#1e293b' }}>
                      {meta.label}
                    </div>
                    <div style={{ fontSize: 12.5, color: '#64748b', fontWeight: 500, marginTop: 2 }}>{meta.hint}</div>
                  </div>
                  <i
                    className={active ? 'pi pi-check-circle' : 'pi pi-circle'}
                    style={{ fontSize: 18, color: active ? meta.color : '#cbd5e1' }}
                  />
                </button>
              );
            })}
          </div>

          {/* ── Student ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-user" title="Student" />

            {student ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  padding: '12px 14px',
                  borderRadius: 12,
                  background: 'linear-gradient(135deg, rgba(30,58,138,.05) 0%, rgba(59,130,246,.08) 100%)',
                  border: '1px solid rgba(59,130,246,.2)',
                }}
              >
                {student.photoUrl ? (
                  <img src={student.photoUrl} alt="" style={studentAvatarStyle} />
                ) : (
                  <div style={studentAvatarStyle}>{getInitials(student.name)}</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b' }}>{student.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginTop: 2 }}>
                    {[classLine(student), student.spStudentId].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setStudent(null)}
                  disabled={saving}
                  style={{
                    padding: '7px 12px',
                    borderRadius: 8,
                    border: '1.5px solid #3b82f6',
                    background: 'white',
                    color: '#3b82f6',
                    fontSize: 12.5,
                    fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <FieldLabel text="Scan ID card or find the student" required />
                <div className={`gatepass-search ${showError('student') ? 'invalid' : ''}`}>
                  <i className="pi pi-qrcode" style={{ fontSize: 15, color: '#3b82f6' }} />
                  <input
                    type="text"
                    autoFocus
                    placeholder="Scan the ID card, or type name, SP ID or roll no"
                    value={searchQuery}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (!saving) pickFromScan();
                      }
                    }}
                    disabled={saving}
                  />
                  {searching && (
                    <ProgressSpinner style={{ width: 18, height: 18 }} strokeWidth="5" animationDuration=".8s" />
                  )}
                </div>
                {showError('student') && (
                  <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6, fontWeight: 500 }}>
                    Pick the student this pass is for
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div
                    style={{
                      marginTop: 8,
                      padding: 6,
                      borderRadius: 12,
                      border: '1px solid #e5e7eb',
                      background: '#f8fafc',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      maxHeight: 280,
                      overflowY: 'auto',
                    }}
                  >
                    {searchResults.map((s) => (
                      <button
                        key={`${s.studentId}-${s.className}-${s.section}`}
                        type="button"
                        className="gatepass-result"
                        onClick={() => pickStudent(s)}
                      >
                        {s.photoUrl ? (
                          <img src={s.photoUrl} alt="" style={{ ...studentAvatarStyle, width: 34, height: 34 }} />
                        ) : (
                          <div style={{ ...studentAvatarStyle, width: 34, height: 34, fontSize: 12 }}>
                            {getInitials(s.name)}
                          </div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13.5, fontWeight: 600, color: '#1f2937' }}>{s.name}</div>
                          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>
                            {[classLine(s), s.spStudentId].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {!searching && searchQuery.trim().length >= 2 && searchResults.length === 0 && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: '#94a3b8', fontWeight: 500 }}>
                    No students matched "{searchQuery.trim()}".
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Reason (+ return time for short leave) ── */}
          <div style={sectionCardStyle}>
            <SectionHeader icon="pi pi-bookmark" title={form.passType === 'short_leave' ? 'Why & Back By' : 'Why'} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {form.passType === 'short_leave' && (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 12,
                    background: PASS_TYPE_META.short_leave.bg,
                    border: `1px solid ${PASS_TYPE_META.short_leave.border}`,
                  }}
                >
                  <FieldLabel text="Back by (optional)" />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {RETURN_QUICK_PICKS.map((q) => (
                      <button
                        key={q.minutes}
                        type="button"
                        className={`gatepass-quick ${quickPick === q.minutes && form.returnTime ? 'active' : ''}`}
                        disabled={saving}
                        onClick={() => pickReturnIn(q.minutes)}
                      >
                        {q.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`gatepass-quick ${!form.returnTime ? 'active' : ''}`}
                      disabled={saving}
                      onClick={() => {
                        setField('returnTime', '');
                        setQuickPick(null);
                      }}
                    >
                      Not sure
                    </button>
                    <span style={{ width: 1, height: 24, background: 'rgba(15,118,110,.25)', margin: '0 2px' }} />
                    <input
                      type="time"
                      className={`gatepass-time ${showError('returnTime') ? 'invalid' : ''}`}
                      value={form.returnTime}
                      onChange={(e) => {
                        setField('returnTime', e.target.value);
                        setQuickPick(null);
                      }}
                      disabled={saving}
                    />
                  </div>
                  {showError('returnTime') && (
                    <div style={{ fontSize: 12, color: '#ef4444', marginTop: 6, fontWeight: 500 }}>
                      Pick a time later than now, or choose "Not sure".
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 8, fontWeight: 500 }}>
                    {form.returnTime
                      ? `Shows as overdue if not back by ${clock12(form.returnTime)}.`
                      : 'No time set — the pass just stays "out" until the student comes back.'}
                  </div>
                </div>
              )}

              <div>
                <FieldLabel text="Reason" required />
                <PillGroup
                  options={REASON_OPTIONS.map((r) => ({ value: r, label: r }))}
                  value={form.reason}
                  invalid={showError('reason')}
                  disabled={saving}
                  onChange={(v) => setField('reason', v)}
                />
              </div>

              <GlobalTextArea
                label="Reason details"
                value={form.reasonDetails}
                onChange={(e) => setField('reasonDetails', e.target.value)}
                placeholder="Anything worth recording, e.g. which doctor or what the emergency is"
                rows={2}
                autoResize
                disabled={saving}
              />
            </div>
          </div>

          {/* ── Pickup ── */}
          <div style={sectionCardStyle}>
            <SectionHeader
              icon="pi pi-users"
              title={form.passType === 'short_leave' ? 'Goes Out With' : 'Picked Up By'}
            />

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <FieldLabel text={form.passType === 'short_leave' ? 'Goes with' : 'Relation to student'} required />
                <PillGroup
                  options={relationOptions.map((r) => ({
                    value: r,
                    label: r === RELATION_ALONE ? 'Alone' : r,
                    icon: r === RELATION_ALONE ? 'pi pi-user' : undefined,
                  }))}
                  value={form.relation}
                  invalid={showError('relation')}
                  disabled={saving}
                  onChange={(v) => setField('relation', v)}
                />
              </div>

              {isAlone ? (
                <div
                  style={{
                    padding: '12px 14px',
                    borderRadius: 12,
                    background: 'rgba(248,250,252,.9)',
                    border: '1px solid rgba(226,232,240,.9)',
                    fontSize: 13,
                    color: '#475569',
                    fontWeight: 500,
                  }}
                >
                  <i className="pi pi-info-circle" style={{ marginRight: 8, color: '#64748b' }} />
                  The student goes out on their own — no name or contact needed.
                </div>
              ) : (
                <>
                  {pickupOptions.length > 0 && (
                    <div>
                      <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, marginBottom: 8 }}>
                        Parent on record
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {pickupOptions.map((p) => (
                          <button
                            key={p.relation}
                            type="button"
                            className="gatepass-quickfill"
                            disabled={saving}
                            onClick={() => fillPickup(p.name, p.relation, p.contactNo)}
                          >
                            <i className="pi pi-user-plus" style={{ fontSize: 12 }} />
                            {p.relation}: {p.name}
                            {p.contactNo && <span style={{ opacity: 0.7, fontWeight: 500 }}>· {p.contactNo}</span>}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <GlobalInputField
                      label="Name"
                      required
                      value={form.pickedUpBy}
                      onChange={(e) => setField('pickedUpBy', e.target.value)}
                      placeholder="e.g. Rakesh Singh"
                      error={showError('pickedUpBy') ? 'Who is the student going with?' : ''}
                      icon="pi pi-user"
                      disabled={saving}
                    />
                    <GlobalInputField
                      label="Contact number"
                      required
                      value={form.contactNo}
                      onChange={(e) => setField('contactNo', e.target.value)}
                      placeholder="e.g. 98765 43210"
                      error={showError('contactNo') ? 'Contact number is required' : ''}
                      icon="pi pi-phone"
                      disabled={saving}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {touched && !canSave && (
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: '#b91c1c',
                background: 'rgba(239,68,68,.08)',
                border: '1px solid rgba(239,68,68,.2)',
                borderRadius: 12,
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <i className="pi pi-exclamation-circle" />
              Fill in the required fields before saving.
            </div>
          )}

          <div style={{ textAlign: 'center', marginTop: 4 }}>
            <GlobalButton
              label={
                saving
                  ? 'Saving...'
                  : form.passType === 'short_leave'
                    ? 'Save & mark gone out'
                    : typeMeta
                      ? `Create ${typeMeta.label.toLowerCase()}`
                      : 'Create gate pass'
              }
              onClick={handleSave}
              variant="primary"
              size="medium"
              disabled={saving}
              loading={saving}
              fullWidth={false}
              icon="pi pi-check"
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default GatePassFormModal;